import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AutocompleteInput,
  BooleanInput,
  Form,
  Loading,
  NumberInput,
  ReferenceInput,
  SelectInput,
  TextInput,
  maxValue,
  minValue,
  number,
  required,
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
  useRefresh,
  useRedirect,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { type AccessibleRegion } from '../auth/permissions';
import type {
  AdminDataProvider,
  ChannelType,
  StoreDetail,
  StoreDetailChannel,
  StoreDetailMarket,
  StoreTag,
} from '../dataProvider';
import { CHANNEL_TYPE_LABELS, CHANNEL_TYPE_VALUES } from '../dataProvider';

/**
 * Stores edit — the multi-channel catalog editor (Phase 8 lead,
 * D-multi-channel-editor). The brand + per-market form (D2-3) plus two catalog
 * sections per editable market: CHANNELS (the per-channel cashback rate, active
 * flag, label, tracking URL and attribution override, and an add-a-channel
 * affordance over the channelType enum) and TAGS (attach / detach the
 * per-market tag assignments). Channels and tags were read-only this far (D2-4);
 * this surface makes them editable.
 *
 * AUDIT FOR FREE: there is deliberately NO audit code here. Every write — brand
 * fields to `merchants`, each changed market's rate/status to THAT market's
 * `merchant_regions` row, a channel update/create to `storeChannels`, a tag
 * attach/detach to `merchantRegionTags` — goes through the audit wrapper
 * (withAudit, #25), which records each with the correct resource / recordId /
 * diff. A whole new editing surface gets full audit with zero audit logic.
 *
 * MARKET-ROW CORRECTNESS (D2-6): which row a section writes is determined by the
 * SECTION (its `regionId` / the channel's own id, captured from the loaded
 * record), never by the region switcher. Editing the UK section writes the UK
 * row; a channel edit writes that channel by id; the switcher cannot
 * cross-contaminate it.
 *
 * THE TAG MIRROR (D-multi-channel-editor): tags are attached / detached on the
 * `merchant_region_tags` JOIN (the source of truth). The denormalised `tagKeys`
 * mirror is DERIVED from the join on read by the provider, so it cannot drift —
 * the editor never writes tagKeys.
 *
 * The brand + per-market rate edit, the per-channel rate edit, and the tag
 * assignment all sit at the SAME `stores:write` tier: a catalog rate is an
 * editor-level descriptive write, market and channel alike (the rate-affecting
 * trust elevation belongs to the rate-rules layer, not the base catalog rates).
 *
 * The edit route is GUARDED `stores:write` (not merely hidden), so a viewer
 * hitting the URL directly is denied.
 */

const splitFounderNames = (text: unknown): string[] =>
  String(text ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

/** A well-formed URL, or undefined to mean "valid". Empty is allowed (required handles presence). */
const validUrl = (value: unknown): string | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  try {
    new URL(String(value));
    return undefined;
  } catch {
    return 'Must be a valid URL';
  }
};

const rateValidators = [
  required(),
  number(),
  minValue(0, 'Rate cannot be negative'),
  maxValue(100, 'Rate looks too high'),
];

/** A plain-number rate is valid when present and within 0..100 (the channel rate). */
const rateError = (value: string): string | null => {
  if (value.trim() === '') return 'Rate is required';
  const n = Number(value);
  if (!Number.isFinite(n)) return 'Rate must be a number';
  if (n < 0) return 'Rate cannot be negative';
  if (n > 100) return 'Rate looks too high';
  return null;
};

/**
 * One editable channel row (D-multi-channel-editor). Edits write the channel BY
 * ITS OWN ID (the section-writes-its-own-row discipline, D2-6) through the
 * provider, audited for free; `isActive=false` DISABLES a channel (there is no
 * hard delete). The channelType is the channel identity and is shown read-only.
 */
const ChannelRow = ({
  channel,
  onSaved,
}: {
  channel: StoreDetailChannel;
  onSaved: () => void;
}) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const [rate, setRate] = useState(String(channel.baseRate));
  const [label, setLabel] = useState(channel.label);
  const [trackingUrl, setTrackingUrl] = useState(channel.trackingUrl ?? '');
  const [isActive, setIsActive] = useState(channel.isActive);
  const [saving, setSaving] = useState(false);
  const err = rateError(rate);

  const onSave = async () => {
    if (err) return;
    setSaving(true);
    try {
      await dataProvider.update('storeChannels', {
        id: channel.id,
        data: {
          baseRate: Number(rate),
          label,
          trackingUrl: trackingUrl.trim() === '' ? null : trackingUrl.trim(),
          isActive,
        },
        previousData: {
          id: channel.id,
          baseRate: channel.baseRate,
          label: channel.label,
          trackingUrl: channel.trackingUrl,
          isActive: channel.isActive,
        },
      });
      notify('Channel updated', { type: 'success' });
      onSaved();
    } catch {
      notify('Could not save the channel', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <TableRow data-testid={`channel-row-${channel.id}`}>
      <TableCell>{CHANNEL_TYPE_LABELS[channel.channelType]}</TableCell>
      <TableCell>
        <TextField
          size="small"
          label="Rate (%)"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          error={err !== null}
          helperText={err ?? ' '}
          inputProps={{ 'data-testid': `channel-rate-${channel.id}` }}
          sx={{ width: 120 }}
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          label="Label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          sx={{ width: 140 }}
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          label="Tracking URL"
          value={trackingUrl}
          onChange={(e) => setTrackingUrl(e.target.value)}
          placeholder="Minted at click time"
          sx={{ width: 200 }}
        />
      </TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
          }
          label={isActive ? 'Active' : 'Disabled'}
        />
      </TableCell>
      <TableCell>
        <Button
          size="small"
          variant="outlined"
          disabled={saving || err !== null}
          onClick={onSave}
          data-testid={`channel-save-${channel.id}`}
        >
          Save channel
        </Button>
      </TableCell>
    </TableRow>
  );
};

/**
 * Add-a-channel affordance (D-multi-channel-editor). Only channel types NOT
 * already present on this market row are offered, enforcing the unique
 * (merchantRegionId, channelType) constraint; the submit re-checks it so a
 * bypass is still rejected. A new channel writes a `storeChannels` row, audited.
 */
const AddChannel = ({
  market,
  onAdded,
}: {
  market: StoreDetailMarket;
  onAdded: () => void;
}) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const present = new Set(market.channels.map((c) => c.channelType));
  const available = CHANNEL_TYPE_VALUES.filter((t) => !present.has(t));
  const [channelType, setChannelType] = useState<ChannelType | ''>('');
  const [rate, setRate] = useState('');
  const [saving, setSaving] = useState(false);

  if (available.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Every channel type is already in use for this market.
      </Typography>
    );
  }

  const err = rateError(rate);

  const onAdd = async () => {
    if (channelType === '' || err) return;
    // Defence in depth: never create a duplicate channel type for this row.
    if (present.has(channelType)) {
      notify('That channel already exists for this market', { type: 'error' });
      return;
    }
    setSaving(true);
    try {
      await dataProvider.create('storeChannels', {
        data: {
          merchantRegionId: market.regionId,
          market: market.market,
          channelType,
          label: CHANNEL_TYPE_LABELS[channelType],
          baseRate: Number(rate),
          trackingUrl: null,
          attributionMethodOverride: null,
          isActive: true,
          createdAt: new Date().toISOString(),
        },
      });
      notify('Channel added', { type: 'success' });
      setChannelType('');
      setRate('');
      onAdded();
    } catch {
      notify('Could not add the channel', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mt: 2 }}>
      <FormControl
        size="small"
        sx={{ minWidth: 180 }}
        data-testid={`add-channel-${market.regionId}`}
      >
        <InputLabel id={`add-channel-type-${market.regionId}`}>
          Add channel
        </InputLabel>
        <Select
          labelId={`add-channel-type-${market.regionId}`}
          label="Add channel"
          value={channelType}
          onChange={(e) => setChannelType(e.target.value as ChannelType)}
        >
          {available.map((t) => (
            <MenuItem key={t} value={t}>
              {CHANNEL_TYPE_LABELS[t]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        size="small"
        label="Rate (%)"
        value={rate}
        onChange={(e) => setRate(e.target.value)}
        error={rate !== '' && err !== null}
        helperText={rate !== '' ? (err ?? ' ') : ' '}
        inputProps={{ 'data-testid': `add-channel-rate-${market.regionId}` }}
        sx={{ width: 120 }}
      />
      <Button
        variant="contained"
        disabled={saving || channelType === '' || err !== null}
        onClick={onAdd}
        data-testid={`add-channel-save-${market.regionId}`}
        sx={{ mt: 0.5 }}
      >
        Add
      </Button>
    </Stack>
  );
};

/**
 * Tag attach / detach for one market section (D-multi-channel-editor). Attaching
 * creates a `merchantRegionTags` join row; detaching deletes it. The join is the
 * source of truth; the provider derives the `tagKeys` mirror from it on read, so
 * the editor never touches tagKeys and the mirror cannot drift.
 */
const TagSection = ({
  market,
  vocabulary,
  onChanged,
}: {
  market: StoreDetailMarket;
  vocabulary: StoreTag[];
  onChanged: () => void;
}) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const [tagId, setTagId] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);

  const assignedTagIds = new Set(market.tags.map((t) => t.tagId));
  const attachable = vocabulary.filter((t) => !assignedTagIds.has(t.id));

  const attach = async () => {
    if (tagId === '') return;
    setBusy(true);
    try {
      await dataProvider.create('merchantRegionTags', {
        data: { merchantRegionId: market.regionId, tagId },
      });
      notify('Tag attached', { type: 'success' });
      setTagId('');
      onChanged();
    } catch {
      notify('Could not attach the tag', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const detach = async (assignmentId: number, tId: number) => {
    setBusy(true);
    try {
      await dataProvider.delete('merchantRegionTags', {
        id: assignmentId,
        previousData: {
          id: assignmentId,
          merchantRegionId: market.regionId,
          tagId: tId,
        },
      });
      notify('Tag removed', { type: 'success' });
      onChanged();
    } catch {
      notify('Could not remove the tag', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box data-testid={`tag-section-${market.regionId}`}>
      {market.tags.length > 0 ? (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {market.tags.map((t) => (
            <Chip
              key={t.assignmentId}
              size="small"
              label={t.label}
              variant="outlined"
              onDelete={
                busy ? undefined : () => detach(t.assignmentId, t.tagId)
              }
              data-testid={`tag-chip-${market.regionId}-${t.tagId}`}
            />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No tags.
        </Typography>
      )}

      {attachable.length > 0 && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <FormControl
            size="small"
            sx={{ minWidth: 220 }}
            data-testid={`attach-tag-${market.regionId}`}
          >
            <InputLabel id={`attach-tag-label-${market.regionId}`}>
              Attach tag
            </InputLabel>
            <Select
              labelId={`attach-tag-label-${market.regionId}`}
              label="Attach tag"
              value={tagId === '' ? '' : String(tagId)}
              onChange={(e) => setTagId(Number(e.target.value))}
            >
              {attachable.map((t) => (
                <MenuItem key={t.id} value={String(t.id)}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            disabled={busy || tagId === ''}
            onClick={attach}
            data-testid={`attach-tag-save-${market.regionId}`}
          >
            Attach
          </Button>
        </Stack>
      )}
    </Box>
  );
};

/**
 * The catalog sections (channels + tags) per editable market, below the brand +
 * per-market form. Writes are immediate and audited; a refresh re-reads the
 * assembled store so the sections reflect the change (the derived tagKeys mirror
 * follows the join automatically). Region-scoped: only the markets the user may
 * see are editable (D2-2).
 */
const StoreCatalogEditor = ({ record }: { record: StoreDetail }) => {
  const { accessibleRegions } = useRegion();
  const refresh = useRefresh();
  const { data: vocabulary = [] } = useGetList<StoreTag>('storeTags', {
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'label', order: 'ASC' },
  });

  const visibleMarkets = record.markets.filter((m) =>
    accessibleRegions.includes(m.market.toUpperCase() as AccessibleRegion),
  );

  return (
    <>
      {visibleMarkets.map((m) => (
        <Card key={`catalog-${m.regionId}`} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
              {m.market.toUpperCase()} channels
            </Typography>
            {m.channels.length > 0 ? (
              <Table size="small" aria-label={`${m.market} channels`}>
                <TableHead>
                  <TableRow>
                    <TableCell>Channel</TableCell>
                    <TableCell>Rate</TableCell>
                    <TableCell>Label</TableCell>
                    <TableCell>Tracking URL</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {m.channels.map((c) => (
                    <ChannelRow key={c.id} channel={c} onSaved={refresh} />
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No channels.
              </Typography>
            )}
            <AddChannel market={m} onAdded={refresh} />

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
              {m.market.toUpperCase()} tags
            </Typography>
            <TagSection
              market={m}
              vocabulary={vocabulary}
              onChanged={refresh}
            />
          </CardContent>
        </Card>
      ))}
    </>
  );
};

interface EditMarket {
  regionId: number;
  market: string;
  baseRate: number;
  active: boolean;
}

interface StoreFormValues {
  name: string;
  logoUrl: string;
  categoryId: number;
  description: string;
  founderNamesText: string;
  countryOfHeritage: string;
  blackOwnershipEvidenceUrl: string;
  ownershipVerificationStatus: string;
  markets: { baseRate: number; active: boolean }[];
}

const StoreEditForm = ({ record }: { record: StoreDetail }) => {
  const { accessibleRegions } = useRegion();
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();

  // D2-2: edit only the market sections the user may see.
  const visibleMarkets: EditMarket[] = record.markets
    .filter((m) =>
      accessibleRegions.includes(m.market.toUpperCase() as AccessibleRegion),
    )
    .map((m) => ({
      regionId: m.regionId,
      market: m.market,
      baseRate: m.baseRate,
      active: m.active,
    }));

  const defaultValues: StoreFormValues = {
    name: record.name,
    logoUrl: record.logoUrl ?? '',
    categoryId: record.categoryId ?? 0,
    description: record.description,
    founderNamesText: record.founderNames.join(', '),
    countryOfHeritage: record.countryOfHeritage,
    blackOwnershipEvidenceUrl: record.blackOwnershipEvidenceUrl,
    ownershipVerificationStatus: record.ownershipVerificationStatus,
    markets: visibleMarkets.map((m) => ({
      baseRate: m.baseRate,
      active: m.active,
    })),
  };

  const onSubmit = async (raw: Record<string, unknown>) => {
    const values = raw as unknown as StoreFormValues;

    // Brand fields → merchants (one entry iff something changed).
    const brandData = {
      name: values.name,
      logoUrl: values.logoUrl,
      categoryId: Number(values.categoryId),
      description: values.description,
      founderNames: splitFounderNames(values.founderNamesText),
      countryOfHeritage: values.countryOfHeritage,
      blackOwnershipEvidenceUrl: values.blackOwnershipEvidenceUrl,
      ownershipVerificationStatus: values.ownershipVerificationStatus,
    };
    const brandPrev = {
      name: record.name,
      logoUrl: record.logoUrl ?? '',
      categoryId: record.categoryId ?? 0,
      description: record.description,
      founderNames: record.founderNames,
      countryOfHeritage: record.countryOfHeritage,
      blackOwnershipEvidenceUrl: record.blackOwnershipEvidenceUrl,
      ownershipVerificationStatus: record.ownershipVerificationStatus,
    };

    const updates: Promise<unknown>[] = [];
    if (JSON.stringify(brandData) !== JSON.stringify(brandPrev)) {
      updates.push(
        dataProvider.update('merchants', {
          id: record.id,
          data: brandData,
          previousData: brandPrev,
        }),
      );
    }

    // Per-market rate/active → THAT market's merchant_regions row (D2-6).
    visibleMarkets.forEach((orig, i) => {
      const next = values.markets[i];
      const newRate = Number(next.baseRate);
      const newActive = Boolean(next.active);
      if (newRate !== orig.baseRate || newActive !== orig.active) {
        updates.push(
          dataProvider.update('merchantRegions', {
            id: orig.regionId,
            data: { baseRate: newRate, active: newActive },
            previousData: {
              id: orig.regionId,
              baseRate: orig.baseRate,
              active: orig.active,
            },
          }),
        );
      }
    });

    try {
      await Promise.all(updates);
      notify('Store updated', { type: 'success' });
      redirect('show', 'stores', record.id);
    } catch {
      notify('Could not save the store', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        Edit {record.name}
      </Typography>
      <Form defaultValues={defaultValues} onSubmit={onSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
              Brand
            </Typography>
            <TextInput
              source="name"
              label="Name"
              validate={required()}
              fullWidth
            />
            <TextInput
              source="logoUrl"
              label="Logo URL"
              validate={[required(), validUrl]}
              fullWidth
            />
            <ReferenceInput source="categoryId" reference="categories">
              <AutocompleteInput
                label="Category"
                optionText="name"
                validate={required()}
              />
            </ReferenceInput>
            <TextInput
              source="description"
              label="Description"
              validate={required()}
              multiline
              fullWidth
            />
            <TextInput
              source="founderNamesText"
              label="Founder(s) — comma separated"
              validate={required()}
              fullWidth
              helperText="e.g. Ada Okafor, Kwame Mensah"
            />
            <TextInput
              source="countryOfHeritage"
              label="Country of heritage"
              validate={required()}
              fullWidth
            />
            <TextInput
              source="blackOwnershipEvidenceUrl"
              label="Black-ownership evidence URL"
              validate={[required(), validUrl]}
              fullWidth
            />
            <SelectInput
              source="ownershipVerificationStatus"
              label="Ownership verification"
              validate={required()}
              choices={[
                { id: 'unverified', name: 'Unverified' },
                { id: 'pending', name: 'Pending' },
                { id: 'verified', name: 'Verified' },
              ]}
            />
          </CardContent>
        </Card>

        {visibleMarkets.map((m, i) => (
          <Card key={m.regionId} sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                {m.market.toUpperCase()} market
              </Typography>
              <NumberInput
                source={`markets.${i}.baseRate`}
                label="Cashback rate (%)"
                validate={rateValidators}
              />
              <BooleanInput source={`markets.${i}.active`} label="Active" />
            </CardContent>
          </Card>
        ))}

        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            Save
          </Button>
          <Button
            type="button"
            onClick={() => redirect('show', 'stores', record.id)}
          >
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

/** The edit view. Exported for testing without the access wrapper. */
export const StoreEditView = () => {
  const { id } = useParams();
  const { data: record, isLoading } = useGetOne<StoreDetail>('stores', {
    id: Number(id),
  });
  if (isLoading || !record) return <Loading />;
  return (
    <>
      <StoreEditForm record={record} />
      <Box sx={{ maxWidth: 720 }}>
        <StoreCatalogEditor record={record} />
      </Box>
    </>
  );
};

/**
 * Gated entry point registered as the `stores` Resource's edit. GUARDED
 * `stores:write` — a viewer hitting /stores/:id/edit directly is denied, not
 * just missing the button.
 */
export const StoreEdit = () => (
  <RequireAccess capability="stores:write">
    <StoreEditView />
  </RequireAccess>
);
